import React, { useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';

/* ----------------------------------------------------------------------------
 * Architecture data
 * ------------------------------------------------------------------------- */

interface Stage {
  id: string;
  label: string;
  kind: 'stem' | 'block' | 'refine' | 'rgb';
  op: string;
  outShape: string;
  params: number;
  intuition: string;
}

const TOTAL_PARAMS = 228_958;
const QUANTIZED_KB = 178;

const STAGES: Stage[] = [
  {
    id: 'stem',
    label: 'Stem',
    kind: 'stem',
    op: 'nn.Linear(28, 1728) → reshape (B, 36, 6, 8) → sin()',
    outShape: '(B, 36, 6, 8)',
    params: 50_112,
    intuition:
      'Lifts the 28-dim latent into a tiny 6×8 feature grid with 36 channels. The sine activation sets up the periodic basis the upsampler will sculpt. This single Linear is the largest tensor in the network at almost a quarter of the budget.',
  },
  {
    id: 'block0',
    label: 'Block 0',
    kind: 'block',
    op: 'Conv2d(36, 144, 3×3, pad=1) + Identity skip + PixelShuffle(2) + sin()',
    outShape: '(B, 36, 12, 16)',
    params: 46_800,
    intuition:
      'First upsample. Conv expands to 144 channels so PixelShuffle(2) can fold them back into 36 channels at 2× resolution. The identity skip carries low-frequency content untouched.',
  },
  {
    id: 'block1',
    label: 'Block 1',
    kind: 'block',
    op: 'Conv2d(36, 144, 3×3, pad=1) + Identity skip + PixelShuffle(2) + sin()',
    outShape: '(B, 36, 24, 32)',
    params: 46_800,
    intuition:
      'Second 2× upsample at full 36-channel width. Together with Block 0, this pair eats almost half the parameter budget; the two of them do the heaviest spatial generation.',
  },
  {
    id: 'block2',
    label: 'Block 2',
    kind: 'block',
    op: 'Conv2d(36, 108, 3×3, pad=1) + Conv2d(36, 27, 1×1) skip + PixelShuffle(2) + sin()',
    outShape: '(B, 27, 48, 64)',
    params: 35_100 + 999,
    intuition:
      'Channels start tapering: 36 → 27 via the 1×1 skip, 108 → 27 after PixelShuffle. The taper begins here because each later channel costs less per parameter once spatial resolution explodes.',
  },
  {
    id: 'block3',
    label: 'Block 3',
    kind: 'block',
    op: 'Conv2d(27, 80, 3×3, pad=1) + Conv2d(27, 20, 1×1) skip + PixelShuffle(2) + sin()',
    outShape: '(B, 20, 96, 128)',
    params: 19_520 + 560,
    intuition:
      'Drops to 20 channels at 96×128. The network is now painting mid-frequency structure: lane edges, vehicle silhouettes, road-vs-sky.',
  },
  {
    id: 'block4',
    label: 'Block 4',
    kind: 'block',
    op: 'Conv2d(20, 72, 3×3, pad=1) + Conv2d(20, 18, 1×1) skip + PixelShuffle(2) + sin()',
    outShape: '(B, 18, 192, 256)',
    params: 13_032 + 378,
    intuition:
      'Half-resolution feature map. Channel count settles at 18, the smallest width that still carries enough information to render two frames.',
  },
  {
    id: 'block5',
    label: 'Block 5',
    kind: 'block',
    op: 'Conv2d(18, 72, 3×3, pad=1) + Identity skip + PixelShuffle(2) + sin()',
    outShape: '(B, 18, 384, 512)',
    params: 11_736,
    intuition:
      'Final 2× upsample, hitting the target 384×512 resolution. The skip drops back to identity now that channels are no longer being squeezed.',
  },
  {
    id: 'refine',
    label: 'Refine',
    kind: 'refine',
    op: 'Conv2d(18, 9, 3×3, pad=2, dilation=2) → Conv2d(9, 18, 3×3, pad=1); x = x + 0.1·sin(out)',
    outShape: '(B, 18, 384, 512)',
    params: 1_467 + 1_476,
    intuition:
      'A dilated bottleneck on the residual path. The 0.1·sin gating keeps it gentle: it adds detail without overwhelming the upsampler. Cheap, but rescues a couple PSNR points.',
  },
  {
    id: 'rgb',
    label: 'RGB Heads',
    kind: 'rgb',
    op: 'Two parallel Conv2d(18, 3, 3×3, pad=1) → sigmoid · 255',
    outShape: '2 × (B, 3, 384, 512)',
    params: 489 + 489,
    intuition:
      'Two independent 3×3 conv heads, one per output frame. They share every feature beneath them and only diverge in these last 489 params each. That parameter sharing is what makes encoding two frames almost free.',
  },
];

const MAX_PARAMS = Math.max(...STAGES.map((s) => s.params));

/* ----------------------------------------------------------------------------
 * Styled atoms
 * ------------------------------------------------------------------------- */

const Wrapper = styled.div`
  margin: 2.5rem 0;
  padding: 1.75rem 1.5rem 2rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg-alt);
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.75rem 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border);
  margin-bottom: 1.5rem;
`;

const Title = styled.h3`
  font-family: var(--font-display);
  font-weight: 500;
  font-size: 1.5rem;
  margin: 0;
  letter-spacing: 0.005em;
  color: var(--text);
`;

const Totals = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  font-family: var(--font-mono);
  font-size: 0.72rem;
  color: var(--text-muted);
  letter-spacing: 0.03em;
`;

const TotalItem = styled.span`
  & b {
    color: var(--text);
    font-weight: 500;
  }
`;

const SelectedReadout = styled(motion.span)`
  color: var(--accent);
  & b {
    color: var(--accent);
  }
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  gap: 2rem;
  align-items: start;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`;

const Pipeline = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const StageRow = styled.div`
  display: grid;
  grid-template-columns: 5.5em minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.85rem;

  @media (max-width: 760px) {
    grid-template-columns: 4em minmax(0, 1fr) auto;
    gap: 0.6rem;
  }
`;

const StageIndex = styled.div`
  font-family: var(--font-mono);
  font-size: 0.65rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-muted);
  text-align: right;
  white-space: nowrap;
`;

const Block = styled(motion.button)<{ selected: boolean; kind: Stage['kind'] }>`
  position: relative;
  width: 100%;
  text-align: left;
  padding: 0.75rem 0.9rem;
  border-radius: 4px;
  border: 1px solid ${({ selected }) => (selected ? 'var(--accent)' : 'var(--border)')};
  background: ${({ selected }) => (selected ? 'var(--bg)' : 'var(--bg)')};
  color: var(--text);
  font: inherit;
  cursor: pointer;
  transition: border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease;
  box-shadow: ${({ selected }) =>
    selected ? '0 1px 0 var(--accent), 0 6px 18px -10px rgba(194, 48, 40, 0.45)' : 'none'};

  &:hover {
    border-color: ${({ selected }) => (selected ? 'var(--accent)' : 'var(--text-muted)')};
  }

  &:focus-visible {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 2px rgba(194, 48, 40, 0.18);
  }

  /* Subtle left rule that picks up the kind. */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    background: ${({ selected, kind }) => {
      if (selected) return 'var(--accent)';
      if (kind === 'stem') return 'var(--text-muted)';
      if (kind === 'rgb') return 'var(--text-muted)';
      if (kind === 'refine') return 'var(--border)';
      return 'transparent';
    }};
    border-radius: 4px 0 0 4px;
  }
`;

const BlockTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.75rem;
`;

const BlockLabel = styled.span`
  font-family: var(--font-display);
  font-size: 1.05rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  color: var(--text);
`;

const BlockShape = styled.span`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  color: var(--text-muted);
  white-space: nowrap;
`;

const BlockBar = styled.div`
  margin-top: 0.5rem;
  height: 3px;
  width: 100%;
  background: var(--border);
  border-radius: 2px;
  overflow: hidden;
`;

const BlockBarFill = styled(motion.div)<{ selected: boolean }>`
  height: 100%;
  background: ${({ selected }) => (selected ? 'var(--accent)' : 'var(--text-muted)')};
  border-radius: 2px;
  transition: background-color 140ms ease;
`;

const ParamBadge = styled.div`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  color: var(--text-muted);
  white-space: nowrap;
  text-align: right;
  min-width: 4.6em;
`;

const Connector = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 4px 0;
  margin-left: calc(5.5em + 0.85rem);

  @media (max-width: 760px) {
    margin-left: calc(4em + 0.6rem);
  }

  & .line {
    width: 1px;
    height: 14px;
    background: var(--text-muted);
    opacity: 0.45;
  }
`;

const RgbSplit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-left: calc(5.5em + 0.85rem);
  margin-top: 4px;

  @media (max-width: 760px) {
    margin-left: calc(4em + 0.6rem);
  }

  & .fork {
    height: 18px;
    position: relative;
    & svg {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
    }
  }

  & .heads {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.6rem;
  }
`;

const RgbHead = styled(motion.button)<{ selected: boolean }>`
  position: relative;
  text-align: left;
  padding: 0.6rem 0.75rem;
  border: 1px solid ${({ selected }) => (selected ? 'var(--accent)' : 'var(--border)')};
  background: var(--bg);
  border-radius: 4px;
  cursor: pointer;
  font: inherit;
  color: var(--text);
  transition: border-color 140ms ease, box-shadow 140ms ease;
  box-shadow: ${({ selected }) =>
    selected ? '0 1px 0 var(--accent), 0 6px 18px -10px rgba(194, 48, 40, 0.45)' : 'none'};

  &:hover {
    border-color: ${({ selected }) => (selected ? 'var(--accent)' : 'var(--text-muted)')};
  }

  &:focus-visible {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 2px rgba(194, 48, 40, 0.18);
  }
`;

const RgbHeadLabel = styled.div`
  font-family: var(--font-display);
  font-size: 0.95rem;
  font-weight: 500;
`;

const RgbHeadMono = styled.div`
  margin-top: 0.15rem;
  font-family: var(--font-mono);
  font-size: 0.65rem;
  color: var(--text-muted);
`;

/* Detail panel ------------------------------------------------------------ */

const Detail = styled.div`
  position: sticky;
  top: 1.25rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg);
  padding: 1.15rem 1.25rem 1.25rem;
  min-height: 280px;

  @media (max-width: 760px) {
    position: relative;
    top: 0;
    min-height: 0;
  }
`;

const DetailKicker = styled.div`
  font-family: var(--font-mono);
  font-size: 0.62rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 0.35rem;
`;

const DetailTitle = styled.h4`
  font-family: var(--font-display);
  font-weight: 500;
  font-size: 1.55rem;
  margin: 0 0 0.85rem;
  letter-spacing: 0.005em;
  color: var(--text);
`;

const DetailMetaGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 1rem;
  row-gap: 0.4rem;
  font-family: var(--font-mono);
  font-size: 0.72rem;
  margin-bottom: 1rem;
`;

const MetaKey = styled.div`
  color: var(--text-muted);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-size: 0.62rem;
  align-self: center;
`;

const MetaVal = styled.div`
  color: var(--text);
  word-break: break-word;
`;

const DetailIntuition = styled.p`
  font-family: var(--font-body);
  font-size: 0.92rem;
  line-height: 1.6;
  color: var(--text);
  margin: 0;
`;

const DetailDivider = styled.div`
  height: 1px;
  background: var(--border);
  margin: 0.85rem 0 0.95rem;
`;

const ShareBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 1rem;

  & .track {
    flex: 1;
    height: 4px;
    background: var(--border);
    border-radius: 2px;
    overflow: hidden;
  }

  & .fill {
    height: 100%;
    background: var(--accent);
  }

  & .pct {
    font-family: var(--font-mono);
    font-size: 0.7rem;
    color: var(--text-muted);
    white-space: nowrap;
    min-width: 3.6em;
    text-align: right;
  }
`;

const Hint = styled.div`
  font-family: var(--font-body);
  font-size: 0.85rem;
  color: var(--text-muted);
  line-height: 1.55;
`;

/* ----------------------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------------------- */

const fmt = (n: number) =>
  n.toLocaleString('en-US');

const pct = (n: number, of: number) =>
  ((n / of) * 100);

/* ----------------------------------------------------------------------------
 * Component
 * ------------------------------------------------------------------------- */

const ArchitectureDiagram: React.FC = () => {
  const [selectedId, setSelectedId] = useState<string>('stem');

  const selected = useMemo(
    () => STAGES.find((s) => s.id === selectedId) ?? STAGES[0],
    [selectedId],
  );

  const selectedShare = pct(selected.params, TOTAL_PARAMS);

  // Pipeline stages excluding RGB; RGB rendered as parallel pair.
  const pipelineStages = STAGES.filter((s) => s.kind !== 'rgb');
  const rgbStage = STAGES.find((s) => s.kind === 'rgb')!;

  return (
    <Wrapper>
      <HeaderRow>
        <Title>HNeRV-style decoder</Title>
        <Totals>
          <TotalItem>
            <b>{fmt(TOTAL_PARAMS)}</b> params
          </TotalItem>
          <TotalItem>
            <b>{QUANTIZED_KB} KB</b> after INT8 + brotli
          </TotalItem>
          <TotalItem>
            output <b>2 × 384 × 512</b> RGB
          </TotalItem>
          <AnimatePresence mode="wait">
            <SelectedReadout
              key={selected.id}
              initial={{ opacity: 0, y: -2 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 2 }}
              transition={{ duration: 0.18 }}
            >
              {selected.label}: <b>{selectedShare.toFixed(1)}%</b> of params
            </SelectedReadout>
          </AnimatePresence>
        </Totals>
      </HeaderRow>

      <Body>
        <Pipeline>
          {pipelineStages.map((stage, i) => {
            const isSelected = stage.id === selected.id;
            const widthPct = (stage.params / MAX_PARAMS) * 100;
            return (
              <React.Fragment key={stage.id}>
                <StageRow>
                  <StageIndex>
                    {stage.kind === 'stem'
                      ? 'input'
                      : stage.kind === 'refine'
                      ? 'refine'
                      : `level ${i}`}
                  </StageIndex>
                  <Block
                    type="button"
                    selected={isSelected}
                    kind={stage.kind}
                    onClick={() => setSelectedId(stage.id)}
                    onMouseEnter={() => {
                      // no-op; selection is on click for accessibility
                    }}
                    whileTap={{ scale: 0.995 }}
                    aria-pressed={isSelected}
                    aria-label={`${stage.label}, ${fmt(stage.params)} params, output ${stage.outShape}`}
                  >
                    <BlockTopRow>
                      <BlockLabel>{stage.label}</BlockLabel>
                      <BlockShape>{stage.outShape}</BlockShape>
                    </BlockTopRow>
                    <BlockBar>
                      <BlockBarFill
                        selected={isSelected}
                        initial={false}
                        animate={{ width: `${widthPct}%` }}
                        transition={{ duration: 0.35, ease: 'easeOut' }}
                      />
                    </BlockBar>
                  </Block>
                  <ParamBadge>{fmt(stage.params)}</ParamBadge>
                </StageRow>
                <Connector aria-hidden="true">
                  <span className="line" />
                </Connector>
              </React.Fragment>
            );
          })}

          {/* RGB heads: the pipeline forks into two parallel conv heads. */}
          <StageRow>
            <StageIndex>output</StageIndex>
            <RgbSplit>
              <div className="fork" aria-hidden="true">
                <svg viewBox="0 0 100 18" preserveAspectRatio="none">
                  <path
                    d="M 50 0 L 50 8 L 18 8 L 18 18 M 50 8 L 82 8 L 82 18"
                    fill="none"
                    stroke="var(--text-muted)"
                    strokeWidth="0.6"
                    opacity="0.5"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
              </div>
              <div className="heads">
                <RgbHead
                  type="button"
                  selected={selected.id === rgbStage.id}
                  onClick={() => setSelectedId(rgbStage.id)}
                  whileTap={{ scale: 0.995 }}
                  aria-pressed={selected.id === rgbStage.id}
                >
                  <RgbHeadLabel>rgb_0</RgbHeadLabel>
                  <RgbHeadMono>frame 0 · 489 params</RgbHeadMono>
                </RgbHead>
                <RgbHead
                  type="button"
                  selected={selected.id === rgbStage.id}
                  onClick={() => setSelectedId(rgbStage.id)}
                  whileTap={{ scale: 0.995 }}
                  aria-pressed={selected.id === rgbStage.id}
                >
                  <RgbHeadLabel>rgb_1</RgbHeadLabel>
                  <RgbHeadMono>frame 1 · 489 params</RgbHeadMono>
                </RgbHead>
              </div>
            </RgbSplit>
            <ParamBadge>{fmt(rgbStage.params)}</ParamBadge>
          </StageRow>
        </Pipeline>

        <Detail>
          <AnimatePresence mode="wait">
            <motion.div
              key={selected.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <DetailKicker>
                {selected.kind === 'stem'
                  ? 'Latent expansion'
                  : selected.kind === 'block'
                  ? 'Pixel-shuffle upsample'
                  : selected.kind === 'refine'
                  ? 'Residual refinement'
                  : 'Output heads'}
              </DetailKicker>
              <DetailTitle>{selected.label}</DetailTitle>

              <DetailMetaGrid>
                <MetaKey>op</MetaKey>
                <MetaVal>{selected.op}</MetaVal>
                <MetaKey>output</MetaKey>
                <MetaVal>{selected.outShape}</MetaVal>
                <MetaKey>params</MetaKey>
                <MetaVal>{fmt(selected.params)}</MetaVal>
              </DetailMetaGrid>

              <DetailIntuition>{selected.intuition}</DetailIntuition>

              <DetailDivider />
              <Hint>Share of total budget</Hint>
              <ShareBar>
                <div className="track">
                  <motion.div
                    className="fill"
                    initial={false}
                    animate={{ width: `${selectedShare}%` }}
                    transition={{ duration: 0.35, ease: 'easeOut' }}
                  />
                </div>
                <span className="pct">{selectedShare.toFixed(1)}%</span>
              </ShareBar>
            </motion.div>
          </AnimatePresence>
        </Detail>
      </Body>
    </Wrapper>
  );
};

export default ArchitectureDiagram;
